import React, { useState } from 'react';
import TextField from 'src/components/Inputs/TextField';
import Text from 'src/components/Text/Text';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import MarketIcon from '../Utils/MarketIcon';
import UI_UTILS from 'src/utils/uiUtils';
import { Info, Swap } from '@drift-labs/icons';
import SlippageTolerance from './SlippageTolerance';
import Tooltip from '../Tooltip/Tooltip';
import { twMerge } from 'tailwind-merge';

const SwapInputSideButton = (props: {
	side: 'base' | 'quote';
	baseAssetSymbol: string;
	marketSymbol: string;
	onClick: () => void;
}) => {
	const [hovered, setHovered] = React.useState(false);

	const handleChangeSide = () => {
		props.onClick();
	};

	return (
		<button
			className="text-text-default flex flex-row items-center px-2 py-1 space-x-2 transition-all bg-button-secondary-bg hover:bg-button-secondary-bg-hover rounded rounded-md border border-button-border"
			onClick={handleChangeSide}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
		>
			<MarketIcon
				marketSymbol={props.side === 'base' ? props.marketSymbol : 'USDC'}
			/>{' '}
			<Text.BODY2>
				{props.side === 'base' ? props.baseAssetSymbol : 'USDC'}
			</Text.BODY2>{' '}
			<span
				className={`transition-all ${
					hovered ? 'text-text-default' : 'text-text-label'
				} relative top-1`}
			>
				<Swap size={20} />
			</span>
		</button>
	);
};

export const SizeSelectorLite = React.memo(function SizeSelectorMemo(props: {
	baseStepSize: number;
	quoteStepSize: number;
	baseSizeStringValue: string;
	quoteSizeStringValue: string;
	maxQuote: number;
	maxBase: number;
	handleChangeSizeBase: (value: string) => void;
	handleChangeSizeQuote: (value: string) => void;
}) {
	// inputSide just controls the larger/editable display value for the lite input field
	// And is completely independent of "leadSide" in the trade form state engine
	const [inputSide, setInputSide] = useState<'base' | 'quote'>('base');
	const marketSymbol = useDriftStore((s) => s.selectedMarket.current.symbol);
	const baseAssetSymbol = useDriftStore((s) =>
		s.selectedMarket.current.baseAssetSymbol(true)
	);

	// Base size rounded to step size
	const roundedBaseSize = UI_UTILS.roundToStepSizeIfLargeEnough(
		props.baseSizeStringValue,
		props.baseStepSize
	);

	const roundedMaxBase = parseFloat(
		UI_UTILS.roundToStepSizeIfLargeEnough(
			`${props.maxBase}`,
			props.baseStepSize
		)
	);

	const maxString =
		inputSide === 'base'
			? `${roundedMaxBase}`
			: UI_UTILS.toFixedLocaleString(props.maxQuote, 2);

	const handleInputChange = (newVal: string) => {
		if (inputSide === 'base') {
			props.handleChangeSizeBase(newVal);
		} else {
			props.handleChangeSizeQuote(newVal);
		}
	};

	const handleMax = () => {
		if (inputSide === 'base') {
			props.handleChangeSizeBase(props.maxBase?.toString() ?? '0');
		} else {
			props.handleChangeSizeQuote(props.maxQuote?.toString() ?? '0');
		}
	};

	return (
		<div className="my-5 relative">
			<div className="flex flex-row items-center justify-between w-full py-0.5 text-text-label">
				<Text.BODY1>Trade Size</Text.BODY1>
				<button
					className="text-text-label bg-button-secondary-bg px-1 leading-4"
					onClick={handleMax}
				>
					<Text.MICRO1>
						Max: {maxString} {inputSide === 'quote' ? 'USDC' : baseAssetSymbol}
					</Text.MICRO1>
				</button>
			</div>
			<div className="mt-2">
				<div>
					<TextField.Hero
						stepSize={
							inputSide === 'base' ? props.baseStepSize : props.quoteStepSize
						}
						depsForOnChange={[
							inputSide,
							props.baseStepSize,
							props.quoteStepSize,
						]}
						type="number"
						onChange={handleInputChange}
						value={
							inputSide === 'base'
								? roundedBaseSize
								: props.quoteSizeStringValue
						}
						placeholder="0"
						subtext={
							inputSide === 'base'
								? `${`$${props.quoteSizeStringValue}` || '$0.00'}`
								: `${`${roundedBaseSize}` || '0'} ${baseAssetSymbol}`
						}
						suffixNode={
							<SwapInputSideButton
								side={inputSide}
								baseAssetSymbol={baseAssetSymbol}
								marketSymbol={marketSymbol}
								onClick={() =>
									setInputSide(inputSide === 'base' ? 'quote' : 'base')
								}
							/>
						}
					/>
				</div>
			</div>
		</div>
	);
});

export const EstEntryPriceLite = React.memo(function EstEntryPriceMemo(props: {
	value: number;
	decimals?: number;
	loading?: boolean;
	warn?: boolean;
}) {
	return (
		<div className="my-5">
			<div className="pb-2">
				<Tooltip
					allowHover
					placement={'right'}
					content={
						<div className="text-text-default">
							<Text.BODY2>
								Estimated entry price is based on the available bid/ask
								liquidity.
								<br /> <br />
								To see detailed bid/ask liquidity in the orderbook, try pro
								mode.
							</Text.BODY2>
						</div>
					}
					className="w-[150px]"
				>
					<Text.BODY2
						className={props.warn ? 'text-warn-yellow' : 'text-text-label'}
					>
						Est. Entry Price
					</Text.BODY2>
					<Info
						className="ml-1"
						color={props.warn ? 'var(--warn-yellow)' : 'var(--text-label)'}
					/>
				</Tooltip>
			</div>
			<div className="bg-container-bg-selected flex flex-row items-center justify-between p-3 w-full">
				<div>
					<Text.H2
						className={twMerge(
							props.value > 0 ? 'text-text-default' : 'invisible',
							'cursor-default'
						)}
					>
						${UI_UTILS.toFixedLocaleString(props.value, props.decimals ?? 4)}
					</Text.H2>
				</div>
			</div>
			<div className="mt-2">
				<SlippageTolerance />
			</div>
		</div>
	);
});

export const LeverageLabelLite = React.memo(function LeverageLabelMemo() {
	return (
		<Tooltip
			allowHover
			placement={'right'}
			content={
				<div className="text-text-default">
					<div className="pb-4">
						<Text.BODY2>
							Leverage available depends on the weighted value of your
							account&apos;s assets and liabilities in Drift&apos;s margin
							system, and the max leverage of the selected market.
						</Text.BODY2>
					</div>
					<a href="#">Learn more</a>
				</div>
			}
			className="w-[100px]"
		>
			<Text.BODY2 className="text-text-label">Leverage</Text.BODY2>
			<Info className="ml-1" color="var(--text-label)" />
		</Tooltip>
	);
});
